import type { FilterOption, FilterType, SelectComponentFilters } from 'types/selectComponent'
import { SlotType } from 'types/slots'
import { V2Component } from './autoSelectTypes'
import { getSupplierAttemptOrder, pickFirstAtPreferredSupplier, selectFromCatalogWithSupplierFallback } from './supplierPreferenceUtils'
import type { DCCableSelectionTrace } from './autoElectricalSelectionTrace'
import { summarizePreferredDCCableReelLengths, componentSupplierKey } from './autoElectricalSelectionTrace'

// --- Reel sizing constants ---------------------------------------------------

/** Smallest reel SKU eligible for inverter auto-select (excludes accessory jumpers). */
export const DC_CABLE_MIN_REEL_LENGTH_METERS = 25
/** Preferred single-reel size for short runs (≤50m total). */
export const DC_CABLE_REEL_SHORT_METERS = 50
export const DC_CABLE_REEL_LONG_METERS = 100
/** Runs ≤ this total only use 50m / 100m reels (e.g. 180m → 2×100m, not 1×250m). */
export const DC_CABLE_MULTI_REEL_MAX_TOTAL_METERS = 200
export const STANDARD_DC_REEL_LENGTH_OPTIONS_METERS = [50, 100, 250, 500, 1000]
/** All reel sizes eligible in the catalog (includes 25m). */
export const INVERTER_DC_CABLE_REEL_LENGTH_OPTIONS_METERS = [25, 50, 100, 250, 500, 1000]

export type DCCablePurchasePlan = {
  reelLength: number
  quantity: number
  purchasedLength: number
}

// --- Purchase-plan math ------------------------------------------------------

export function getMaxReelLengthMetersForRun(totalRunLengthMeters: number): number {
  return totalRunLengthMeters <= DC_CABLE_MULTI_REEL_MAX_TOTAL_METERS
    ? DC_CABLE_REEL_LONG_METERS
    : Number.POSITIVE_INFINITY
}

function sortUniqueReelLengths(lengths: number[]): number[] {
  return [...new Set(lengths)]
    .filter((length) => Number.isFinite(length) && length > 0)
    .sort((a, b) => a - b)
}

/** Reel sizes ≥25m for catalog and purchase-plan math (drops accessory jumpers). */
export function inverterDCCableReelLengths(lengths: number[]): number[] {
  const filtered = sortUniqueReelLengths(lengths).filter((length) => length >= DC_CABLE_MIN_REEL_LENGTH_METERS)
  return filtered.length ? filtered : [...INVERTER_DC_CABLE_REEL_LENGTH_OPTIONS_METERS]
}

function isBetterPurchasePlan(candidate: DCCablePurchasePlan, current: DCCablePurchasePlan): boolean {
  if (candidate.purchasedLength !== current.purchasedLength) {
    return candidate.purchasedLength < current.purchasedLength
  }
  if (candidate.quantity !== current.quantity) {
    return candidate.quantity < current.quantity
  }
  return candidate.reelLength > current.reelLength
}

/**
 * Optimal reel size and quantity for `totalRunLengthMeters`: minimise purchased
 * length, then reel count (1×50m beats 2×25m). Totals ≤ 200m cap at 100m reels.
 */
export function computeDCCablePurchasePlan(
  totalRunLengthMeters: number,
  availableReelLengthsMeters: number[] = INVERTER_DC_CABLE_REEL_LENGTH_OPTIONS_METERS
): DCCablePurchasePlan {
  const reelLengths = inverterDCCableReelLengths(availableReelLengthsMeters)
  const fallbackReel = reelLengths[0] ?? DC_CABLE_REEL_SHORT_METERS

  if (totalRunLengthMeters <= 0) {
    return { reelLength: fallbackReel, quantity: 1, purchasedLength: fallbackReel }
  }

  const maxReelSize = getMaxReelLengthMetersForRun(totalRunLengthMeters)
  let eligible = reelLengths.filter((length) => length <= maxReelSize)
  // 25m is for shorter runs only — never plan stacks of 25m reels on long totals when 50m+ exists.
  if (totalRunLengthMeters > DC_CABLE_REEL_SHORT_METERS) {
    const standardAndUp = eligible.filter((length) => length >= DC_CABLE_REEL_SHORT_METERS)
    if (standardAndUp.length) eligible = standardAndUp
  }
  if (!eligible.length) eligible = reelLengths

  let best: DCCablePurchasePlan | null = null

  for (const reelLength of eligible) {
    const quantity = Math.ceil(totalRunLengthMeters / reelLength)
    const candidate: DCCablePurchasePlan = {
      reelLength,
      quantity,
      purchasedLength: reelLength * quantity,
    }
    if (!best || isBetterPurchasePlan(candidate, best)) {
      best = candidate
    }
  }

  if (best) return best

  const quantity = Math.max(1, Math.ceil(totalRunLengthMeters / fallbackReel))
  return { reelLength: fallbackReel, quantity, purchasedLength: fallbackReel * quantity }
}

/** @deprecated Prefer {@link computeDCCablePurchasePlan}. */
export function getMinimumDCCableReelLengthMeters(
  totalRunLengthMeters: number,
  availableReelLengthsMeters: number[] = INVERTER_DC_CABLE_REEL_LENGTH_OPTIONS_METERS
): number {
  return computeDCCablePurchasePlan(totalRunLengthMeters, availableReelLengthsMeters).reelLength
}

/** Reel lengths to try in order: optimal first, then remaining sizes ascending. */
export function getDCCableReelLengthCandidates(
  totalRunLengthMeters: number,
  availableReelLengthsMeters: number[]
): number[] {
  const reelLengths = inverterDCCableReelLengths(availableReelLengthsMeters)
  if (!reelLengths.length) {
    return [computeDCCablePurchasePlan(totalRunLengthMeters).reelLength]
  }

  const { reelLength: optimal } = computeDCCablePurchasePlan(totalRunLengthMeters, reelLengths)
  return [optimal, ...reelLengths.filter((length) => length !== optimal)]
}

function escalationLengthsFrom(
  totalRunLength: number,
  lengthPool: number[],
  minimumReelLength: number
): number[] {
  const maxReelSize = getMaxReelLengthMetersForRun(totalRunLength)
  return getDCCableReelLengthCandidates(totalRunLength, lengthPool).filter(
    (length) => length >= minimumReelLength && length <= maxReelSize
  )
}

// --- Filter-schema helpers ---------------------------------------------------

export function parseCableLengthOptionsFromFilterSchema(filtersSchema: FilterType[] | null | undefined): number[] {
  const options = filtersSchema?.find((filter) => filter.key === 'cable_length')?.options ?? []
  const fromSchema = options
    .map((option) => (option.value != null ? Number(option.value) : NaN))
    .filter((length) => Number.isFinite(length) && length > 0)

  return inverterDCCableReelLengths(
    fromSchema.length ? fromSchema : [...INVERTER_DC_CABLE_REEL_LENGTH_OPTIONS_METERS]
  )
}

export function pickSmallestFilterOptionAtLeast(
  options: FilterOption[] | undefined,
  minimumMeters: number
): { value: string } | undefined {
  const sorted = [...(options ?? [])]
    .filter((option) => option.value != null && Number.isFinite(Number(option.value)))
    .sort((a, b) => Number(a.value) - Number(b.value))

  const match = sorted.find((option) => Number(option.value) >= minimumMeters)
  return match?.value != null ? { value: String(match.value) } : undefined
}

/**
 * Omit cable_length from the API fetch only — thickness, DC type, and other slot
 * filters stay. Reel length is chosen client-side from total run length; a fixed
 * cable_length in the request would hide larger reels at the preferred supplier.
 */
export function filterSettingsWithoutCableLength(filterSettings: SelectComponentFilters): SelectComponentFilters {
  const { cable_length: _cableLength, ...rest } = filterSettings
  return rest
}

// --- Catalog refinement ------------------------------------------------------

export function resolveReelLengthPool(components: V2Component[], reelLengthOptions: number[]): number[] {
  const fromCatalog = components.map((c) => Number(c.cable_length))
  const merged = [...reelLengthOptions, ...fromCatalog]
  return inverterDCCableReelLengths(merged.length ? merged : [...INVERTER_DC_CABLE_REEL_LENGTH_OPTIONS_METERS])
}

/** Drops accessory SKUs under 25m (e.g. 2m / 10m MC4 jumpers). */
export function filterToDCCableReelSkus(components: V2Component[], reelLengthOptions: number[]): V2Component[] {
  const allowedLengths = new Set(
    inverterDCCableReelLengths(
      reelLengthOptions.length ? reelLengthOptions : [...INVERTER_DC_CABLE_REEL_LENGTH_OPTIONS_METERS]
    )
  )
  return components.filter((component) => allowedLengths.has(Number(component.cable_length)))
}

// --- Supplier + reel selection -----------------------------------------------

export function isInverterLevelDCCableSlot(slot: SlotType): boolean {
  if (slot.type !== 'dc_cable' || !slot.attachToUuid) return false
  return window.editor?.objectByUuid?.(slot.attachToUuid)?.type === 'OsInverter'
}

function pickThinnestDCCableAtLength(components: V2Component[], reelLength: number): V2Component | undefined {
  const matches = components.filter((component) => Number(component.cable_length) === reelLength)
  if (!matches.length) return undefined
  return matches.sort((a, b) => {
    const aThickness = Number(a.cable_thickness)
    const bThickness = Number(b.cable_thickness)
    if (Number.isFinite(aThickness) && Number.isFinite(bThickness) && aThickness !== bThickness) {
      return aThickness - bThickness
    }
    return String(a.code || '').localeCompare(String(b.code || ''))
  })[0]
}

function componentFromSupplierAtLength(
  components: V2Component[],
  supplier: string,
  reelLength: number
): V2Component | undefined {
  const matches = components.filter(
    (component) =>
      Number(component.cable_length) === reelLength &&
      component.ordering_v2?.some((ordering) => ordering.distributor === supplier)
  )
  if (!matches.length) return undefined

  return pickThinnestDCCableAtLength(matches, reelLength)
}

/** First match at `reelLength`, honouring `orderedSuppliers` when provided. */
export function pickDCCableReelAtPreferredSupplier(
  components: V2Component[],
  orderedSuppliers: string[],
  reelLengthMeters: number,
  requirePreferredSupplier = true
): V2Component | null {
  const atLength = components.filter((component) => Number(component.cable_length) === reelLengthMeters)
  return pickFirstAtPreferredSupplier(atLength, orderedSuppliers, requirePreferredSupplier)
}

/** Preferred pass: each supplier in order, escalating reel length (never smaller than needed). */
function pickFromPreferredSuppliers(
  components: V2Component[],
  orderedSuppliers: string[],
  lengthsToTry: number[]
): V2Component | null {
  return pickFromPreferredSuppliersWithWalk(components, orderedSuppliers, lengthsToTry)?.match ?? null
}

type DCCableReelWalkResult = {
  match: V2Component
  supplier: string
  reelLength: number
}

function pickFromPreferredSuppliersWithWalk(
  components: V2Component[],
  orderedSuppliers: string[],
  lengthsToTry: number[]
): DCCableReelWalkResult | null {
  const stockedByPreferred = orderedSuppliers.length
    ? components.filter((component) =>
        component.ordering_v2?.some((ordering) => orderedSuppliers.includes(ordering.distributor))
      )
    : components

  for (const supplier of orderedSuppliers) {
    for (const reelLength of lengthsToTry) {
      const match = componentFromSupplierAtLength(stockedByPreferred, supplier, reelLength)
      if (match) return { match, supplier, reelLength }
    }
  }
  return null
}

/** Fallback pass: walk suppliers in preference order, escalating reel length at each. */
function pickFromFullCatalog(
  components: V2Component[],
  orderedSuppliers: string[],
  lengthsToTry: number[]
): V2Component | null {
  return pickFromFullCatalogWithWalk(components, orderedSuppliers, lengthsToTry)?.match ?? null
}

function pickFromFullCatalogWithWalk(
  components: V2Component[],
  orderedSuppliers: string[],
  lengthsToTry: number[]
): DCCableReelWalkResult | null {
  const stocked = components.filter((component) => component.ordering_v2?.length)
  const suppliersToTry = getSupplierAttemptOrder(stocked, orderedSuppliers, false)

  for (const supplier of suppliersToTry) {
    for (const reelLength of lengthsToTry) {
      const match = componentFromSupplierAtLength(stocked, supplier, reelLength)
      if (match) return { match, supplier, reelLength }
    }
  }
  return null
}

export type DCCableReelPickResult = {
  match: V2Component | null
  trace: DCCableSelectionTrace
}

function buildDCCableSelectionTrace(
  components: V2Component[],
  orderedSuppliers: string[],
  totalRunLength: number,
  reelLengthOptions: number[],
  preferredSuppliersOnly: boolean,
  walkResult: DCCableReelWalkResult | null
): DCCableSelectionTrace {
  const lengthPool = resolveReelLengthPool(components, reelLengthOptions)
  const plan = computeDCCablePurchasePlan(totalRunLength, lengthPool)
  const lengthsToTry = escalationLengthsFrom(totalRunLength, lengthPool, plan.reelLength)
  const preferredStockedLengths = summarizePreferredDCCableReelLengths(components, orderedSuppliers)
  const selectedReelLength = walkResult?.reelLength
  const firstPreferredSupplier = orderedSuppliers[0]

  return {
    totalRunLength,
    plannedReelLength: plan.reelLength,
    plannedQuantity: plan.quantity,
    plannedPurchasedLength: plan.purchasedLength,
    lengthsToTry,
    preferredStockedLengths,
    selectedReelLength,
    selectedSupplier: walkResult?.supplier,
    reelLengthEscalated: selectedReelLength != null && selectedReelLength > plan.reelLength,
    supplierEscalated:
      !!walkResult?.supplier && !!firstPreferredSupplier && walkResult.supplier !== firstPreferredSupplier,
    catalogScopeEscalated: !preferredSuppliersOnly,
  }
}

export function pickInverterDCCableReelWithTrace(
  components: V2Component[],
  orderedSuppliers: string[],
  totalRunLength: number,
  reelLengthOptions: number[],
  preferredSuppliersOnly = true
): DCCableReelPickResult {
  const lengthPool = resolveReelLengthPool(components, reelLengthOptions)
  const { reelLength: neededLength } = computeDCCablePurchasePlan(totalRunLength, lengthPool)
  const lengthsToTry = escalationLengthsFrom(totalRunLength, lengthPool, neededLength)

  const walkResult = preferredSuppliersOnly
    ? pickFromPreferredSuppliersWithWalk(components, orderedSuppliers, lengthsToTry)
    : pickFromFullCatalogWithWalk(components, orderedSuppliers, lengthsToTry)

  return {
    match: walkResult?.match ?? null,
    trace: buildDCCableSelectionTrace(
      components,
      orderedSuppliers,
      totalRunLength,
      reelLengthOptions,
      preferredSuppliersOnly,
      walkResult
    ),
  }
}

/**
 * One inverter → one reel SKU. Preferred suppliers: walk each supplier upward
 * from the needed length. Fallback: same length order across the full catalog.
 */
export function pickInverterDCCableReelFromComponents(
  components: V2Component[],
  orderedSuppliers: string[],
  totalRunLength: number,
  reelLengthOptions: number[],
  preferredSuppliersOnly = true
): V2Component | null {
  return pickInverterDCCableReelWithTrace(
    components,
    orderedSuppliers,
    totalRunLength,
    reelLengthOptions,
    preferredSuppliersOnly
  ).match
}

/** Third tier after stocked supplier walks: compatible reel SKU, ignoring ordering_v2. */
function pickInverterDCCableCatalogOnlyFallback(
  components: V2Component[],
  orderedSuppliers: string[],
  totalRunLength: number,
  reelLengthOptions: number[]
): DCCableReelPickResult | null {
  const lengthPool = resolveReelLengthPool(components, reelLengthOptions)
  const { reelLength: neededLength } = computeDCCablePurchasePlan(totalRunLength, lengthPool)
  const lengthsToTry = escalationLengthsFrom(totalRunLength, lengthPool, neededLength)

  for (const reelLength of lengthsToTry) {
    const match = pickThinnestDCCableAtLength(components, reelLength)
    if (!match) continue

    const walkResult: DCCableReelWalkResult = {
      match,
      supplier: componentSupplierKey(match, orderedSuppliers) ?? 'catalog',
      reelLength,
    }
    return {
      match,
      trace: {
        ...buildDCCableSelectionTrace(
          components,
          orderedSuppliers,
          totalRunLength,
          reelLengthOptions,
          false,
          walkResult
        ),
        catalogUnstockedFallback: true,
      },
    }
  }
  return null
}

// --- Auto-select orchestration -----------------------------------------------

export async function selectInverterDCCableReelMatch(params: {
  filterSettings: SelectComponentFilters
  orderedSuppliers: string[]
  totalRunLength: number
  reelLengthOptions: number[]
  fetchComponents: (settings: SelectComponentFilters, scopedToPreferredSuppliers: boolean) => Promise<V2Component[]>
  refineComponents?: (components: V2Component[]) => V2Component[]
}): Promise<{ match: V2Component | null; totalFound: number; selectionTrace?: DCCableSelectionTrace }> {
  const fetchSettings = filterSettingsWithoutCableLength(params.filterSettings)
  let lastTrace: DCCableSelectionTrace | undefined

  const { match, totalFound, catalogFetchScope } = await selectFromCatalogWithSupplierFallback<
    V2Component,
    DCCableReelPickResult
  >({
    orderedSuppliers: params.orderedSuppliers,
    fetchComponents: (scopedToPreferredSuppliers) =>
      params.fetchComponents(fetchSettings, scopedToPreferredSuppliers),
    refineComponents: params.refineComponents,
    pick: (components, preferredSuppliersOnly) => {
      const result = pickInverterDCCableReelWithTrace(
        components,
        params.orderedSuppliers,
        params.totalRunLength,
        params.reelLengthOptions,
        preferredSuppliersOnly
      )
      lastTrace = {
        ...result.trace,
        catalogScopeEscalated: !preferredSuppliersOnly,
      }
      return result.match ? result : null
    },
    fallbackPick: (components) =>
      pickInverterDCCableCatalogOnlyFallback(
        components,
        params.orderedSuppliers,
        params.totalRunLength,
        params.reelLengthOptions
      ),
  })

  const selectionTrace = (match?.trace ?? lastTrace)
    ? {
        ...(match?.trace ?? lastTrace)!,
        catalogFetchScope,
        catalogScopeEscalated:
          (match?.trace ?? lastTrace)!.catalogScopeEscalated || catalogFetchScope === 'full_catalog',
      }
    : undefined

  return {
    match: match?.match ?? null,
    totalFound,
    selectionTrace,
  }
}
